import { createReducer, on } from '@ngrx/store';
import { AccountLockedException, AccountNotVerifiedException, AuthenticationException, BadCredentialsException } from '@uzurv/rideWss/auth';
import { AuthenticationStatus } from '@uzurv/rideWss/auth/constants';
import * as RiderAuthenticationActions from './rider-authentication.action';
import { RiderAuthenticationState } from './rider-authentication.state';

export const riderAuthenticationFeatureKey = 'riderAuthentication';

export const initialState: RiderAuthenticationState = {
  authenticationStatus: AuthenticationStatus.LOGGED_OUT,
  authenticatedRider: null,
  mobilityProfile: {
    mobilityProfileDetail: null,
    modifiedMobilityProfileDetail: null,
    isUpdating: false
  },
  currentDeviceInstallation: null,
  emailVerificationCredentials: null,
  isPerformingAuthenticationAction: false,
  isRefreshingAuthenticatedRider: false,
  isRehydrated: false,
  riderAddress: null,
  isSavingRiderData: false
};

export const reducer = createReducer(
  initialState,

  on(RiderAuthenticationActions.logoutSuccess, (state, action) => {
    return {
      ...initialState,
      authenticationStatus: AuthenticationStatus.LOGGED_OUT
    };
  }),

  on(RiderAuthenticationActions.refreshAuthenticatedRider, (state, action) => {
    return {
      ...state,
      isRefreshingAuthenticatedRider: true
    };
  }),

  on(RiderAuthenticationActions.refreshAuthenticatedRiderSuccess, (state, action) => {
    return {
      ...state,
      authenticatedRider: action.rider,
      isRefreshingAuthenticatedRider: false
    };
  }),

  on(RiderAuthenticationActions.authenticateRider, RiderAuthenticationActions.forgotPassword, (state, action) => {
    return {
      ...state,
      isPerformingAuthenticationAction: true
    };
  }),

  on(RiderAuthenticationActions.authenticateRiderSuccess, state => {
    return {
      ...state,
      isPerformingAuthenticationAction: false,
      authenticationStatus: AuthenticationStatus.LOGGED_IN
    };
  }),

  on(RiderAuthenticationActions.forgotPasswordSuccess, state => {
    return {
      ...state,
      isPerformingAuthenticationAction: false
    };
  }),

  on(RiderAuthenticationActions.authenticateRiderFailure, RiderAuthenticationActions.forgotPasswordFailure, (state, action) => {
    let authenticationStatus = AuthenticationStatus.LOGGED_OUT;

    if (action.error instanceof AuthenticationException) {
      if (action.error instanceof AccountNotVerifiedException) {
        authenticationStatus = AuthenticationStatus.ACCOUNT_NOT_VERIFIED;
      } else if (action.error instanceof BadCredentialsException) {
        authenticationStatus = AuthenticationStatus.BAD_CREDENTIALS;
      } else if (action.error instanceof AccountLockedException) {
        authenticationStatus = AuthenticationStatus.ACCOUNT_LOCKED;
      }
    }

    return {
      ...state,
      authenticationStatus: authenticationStatus,
      isPerformingAuthenticationAction: false
    };
  }),

  on(RiderAuthenticationActions.resetStore, (state, action) => {
    return {
      ...initialState
    };
  }),

  on(RiderAuthenticationActions.rehydrateStateSuccess, (state, action) => {
    return {
      ...state,
      ...action.state,
      isRehydrated: true
    };
  }),

  on(RiderAuthenticationActions.setCurrentDeviceInstallation, (state, action) => {
    return {
      ...state,
      currentDeviceInstallation: action.currentDeviceInstallation
    };
  }),

  on(RiderAuthenticationActions.setEmailVerificationCredentials, (state, action) => {
    return {
      ...state,
      emailVerificationCredentials: action.emailVerificationCredentials
    };
  }),

  on(RiderAuthenticationActions.checkEmailVerificationSuccess, (state, action) => {
    return {
      ...state,
      emailVerificationCredentials: null
    };
  }),

  on(RiderAuthenticationActions.addRiderAddress, (state, action) => {
    return {
      ...state,
      isSavingRiderData: true
    };
  }),

  on(RiderAuthenticationActions.addRiderAddressSuccess, (state, action) => {
    return {
      ...state,
      riderAddress: action.address,
      isSavingRiderData: false
    };
  }),

  on(RiderAuthenticationActions.fetchRiderAddressSuccess, (state, action) => {
    return {
      ...state,
      riderAddress: action.address
    };
  }),

  on(RiderAuthenticationActions.updateRiderPhoneNumber, RiderAuthenticationActions.updateRiderName, state => {
    return {
      ...state,
      isSavingRiderData: true
    };
  }),

  on(RiderAuthenticationActions.updateRiderPhoneNumberSuccess, RiderAuthenticationActions.updateRiderNameSuccess, (state, action) => {
    return {
      ...state,
      isSavingRiderData: false,
      authenticatedRider: action.rider
    };
  }),

  on(RiderAuthenticationActions.updateRiderPhoneNumberFailure, RiderAuthenticationActions.updateRiderNameFailure, state => {
    return {
      ...state,
      isSavingRiderData: false
    };
  }),

  on(RiderAuthenticationActions.fetchRiderMobilityProfileDetailSuccess, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        mobilityProfileDetail: action.mobilityProfileDetail,
        modifiedMobilityProfileDetail: action.mobilityProfileDetail,
        isUpdating: false
      }
    };
  }),

  on(RiderAuthenticationActions.fetchRiderMobilityProfileDetailFailure, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        isUpdating: false
      }
    };
  }),

  on(RiderAuthenticationActions.updateModifiedMobilityProfileAids, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        modifiedMobilityProfileDetail: {
          ...state.mobilityProfile.modifiedMobilityProfileDetail,
          manualWheelchair: action.mobilityProfileDetail.manualWheelchair,
          powerWheelchair: action.mobilityProfileDetail.powerWheelchair,
          electricScooter: action.mobilityProfileDetail.electricScooter,
          bariatricWheelchair: action.mobilityProfileDetail.bariatricWheelchair,
          hasWalker: action.mobilityProfileDetail.hasWalker,
          hasCane: action.mobilityProfileDetail.hasCane,
          hasRollator: action.mobilityProfileDetail.hasRollator
        }
      }
    };
  }),

  on(RiderAuthenticationActions.updateModifiedMobilityProfileAssistance, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        modifiedMobilityProfileDetail: {
          ...state.mobilityProfile.modifiedMobilityProfileDetail,
          isStairAssistanceRequired: action.mobilityProfileDetail.isStairAssistanceRequired,
          hasServiceAnimal: action.mobilityProfileDetail.hasServiceAnimal,
          hasPersonalCareAssistant: action.mobilityProfileDetail.hasPersonalCareAssistant
        }
      }
    };
  }),

  on(RiderAuthenticationActions.updateModifiedMobilityProfileMedicalHealth, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        modifiedMobilityProfileDetail: {
          ...state.mobilityProfile.modifiedMobilityProfileDetail,
          hasHearingImpairment: action.mobilityProfileDetail.hasHearingImpairment,
          hasVisionImpairment: action.mobilityProfileDetail.hasVisionImpairment,
          hasIntellectualDisability: action.mobilityProfileDetail.hasIntellectualDisability,
          hasPortableOxygen: action.mobilityProfileDetail.hasPortableOxygen
        }
      }
    };
  }),

  on(RiderAuthenticationActions.refreshModifiedMobilityProfileDetail, (state, action) => {
    return {
      ...state,
      mobilityProfile: {
        ...state.mobilityProfile,
        modifiedMobilityProfileDetail: state.mobilityProfile.mobilityProfileDetail
      }
    };
  }),

  on(
    RiderAuthenticationActions.updateRiderMobilityProfileDetail,
    RiderAuthenticationActions.verifyRiderMobilityProfileDetail,
    (state, action) => {
      return {
        ...state,
        mobilityProfile: {
          ...state.mobilityProfile,
          isUpdating: true
        }
      };
    }
  ),

  on(
    RiderAuthenticationActions.updateRiderMobilityProfileDetailSuccess,
    RiderAuthenticationActions.verifyRiderMobilityProfileDetailSuccess,
    RiderAuthenticationActions.updateRiderMobilityProfileDetailFailure,
    RiderAuthenticationActions.verifyRiderMobilityProfileDetailFailure,
    (state, action) => {
      return {
        ...state,
        mobilityProfile: {
          ...state.mobilityProfile,
          isUpdating: false
        }
      };
    }
  )
);
